// EmanetX status sayfasi — public uptime.
// Backend GET /saglik her 30 sn polling, basit ama net.

import { useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_TABAN_URL ?? 'https://api.emanetx.com';
const POLL_MS = 30_000;

type Durum = 'kontrol-ediliyor' | 'ayakta' | 'kapali';

interface Saglik {
  durum: string;
  zaman: string;
  surum: string;
}

export function App() {
  const [durum, setDurum] = useState<Durum>('kontrol-ediliyor');
  const [son, setSon] = useState<Saglik | null>(null);
  const [sonKontrol, setSonKontrol] = useState<Date | null>(null);
  const [gecikme, setGecikme] = useState<number | null>(null);

  useEffect(() => {
    let aktif = true;
    const tikla = async () => {
      const baslangic = Date.now();
      try {
        const yanit = await fetch(`${API_URL}/saglik`, { cache: 'no-store' });
        if (!aktif) return;
        if (!yanit.ok) throw new Error(String(yanit.status));
        const veri = (await yanit.json()) as Saglik;
        setSon(veri);
        setDurum(veri.durum === 'ayakta' ? 'ayakta' : 'kapali');
        setGecikme(Date.now() - baslangic);
      } catch {
        if (!aktif) return;
        setDurum('kapali');
        setGecikme(null);
      }
      setSonKontrol(new Date());
    };
    void tikla();
    const id = setInterval(() => void tikla(), POLL_MS);
    return () => {
      aktif = false;
      clearInterval(id);
    };
  }, []);

  return (
    <main className="min-h-screen bg-zinc-50">
      <div className="mx-auto max-w-2xl px-6 py-16">
        <header className="text-center">
          <h1 className="text-2xl font-extrabold tracking-tight text-zinc-900">
            emanet<span className="text-amber-500">x</span> · Sistem Durumu
          </h1>
          <p className="mt-2 text-sm text-zinc-500">Servislerin canlı sağlık raporu</p>
        </header>

        <section className="mt-10">
          <ServisKart
            ad="API"
            url={API_URL}
            durum={durum}
            son={son}
            sonKontrol={sonKontrol}
            gecikme={gecikme}
          />
        </section>

        <section className="mt-8 rounded-2xl border border-zinc-200 bg-white p-5 text-sm text-zinc-600">
          <h3 className="font-extrabold text-zinc-900">Bildirim</h3>
          <p className="mt-2">
            Bu sayfa her {POLL_MS / 1000} saniyede bir API sağlık kontrolü yapar. Kesinti yaşanırsa{' '}
            <a className="text-amber-600 underline" href="mailto:info@emanetx.com">
              info@emanetx.com
            </a>{' '}
            adresine yazabilirsin.
          </p>
        </section>

        <footer className="mt-10 text-center text-xs text-zinc-400">
          © {new Date().getFullYear()} EmanetX
        </footer>
      </div>
    </main>
  );
}

function ServisKart({
  ad,
  url,
  durum,
  son,
  sonKontrol,
  gecikme,
}: {
  ad: string;
  url: string;
  durum: Durum;
  son: Saglik | null;
  sonKontrol: Date | null;
  gecikme: number | null;
}) {
  const stil = {
    'kontrol-ediliyor': {
      sinif: 'bg-zinc-100 text-zinc-600',
      etiket: 'Kontrol ediliyor...',
      nokta: 'bg-zinc-400',
    },
    ayakta: {
      sinif: 'bg-emerald-50 text-emerald-700',
      etiket: 'Çalışıyor',
      nokta: 'bg-emerald-500',
    },
    kapali: {
      sinif: 'bg-red-50 text-red-700',
      etiket: 'Yanıt vermiyor',
      nokta: 'bg-red-500',
    },
  }[durum];

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-extrabold text-zinc-900">{ad}</h3>
          <p className="mt-0.5 font-mono text-xs text-zinc-400">{url}</p>
        </div>
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${stil.sinif}`}
        >
          <span className={`h-2 w-2 animate-pulse rounded-full ${stil.nokta}`} />
          {stil.etiket}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
        <Veri etiket="Sürüm" deger={son?.surum ?? '—'} />
        <Veri etiket="Yanıt Süresi" deger={gecikme !== null ? `${gecikme} ms` : '—'} />
        <Veri
          etiket="Son Kontrol"
          deger={
            sonKontrol
              ? sonKontrol.toLocaleTimeString('tr-TR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })
              : '—'
          }
        />
      </div>
    </div>
  );
}

function Veri({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div>
      <p className="text-xs text-zinc-500">{etiket}</p>
      <p className="font-mono font-extrabold text-zinc-900">{deger}</p>
    </div>
  );
}
